import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  SummarySection,
} from '@shared/components/add-form/add-form.component';
import { PosShiftCloseService } from './services/pos-shift-close.service';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PosShiftCloseFormConfig {
  title: string;
  formFields: FormField[];
  summarySection: SummarySection;
  headerActions: any[];
  listRoute: string;
}

// Search configuration with dynamic options
export function getPosShiftCloseSearchConfig(
  posShiftCloseService: PosShiftCloseService
): FormFieldConfig[] {
  return [
    {
      key: 'cashierName',
      label: 'pos.shift_close.search.fields.cashier_name',
      type: 'text',
      placeholder: 'pos.shift_close.search.placeholders.enter_cashier_name',
    },
    {
      key: 'posId',
      label: 'pos.shift_close.search.fields.pos_code',
      type: 'select',
      placeholder: 'pos.shift_close.search.placeholders.select_pos_code',
      options: [], // Will be populated dynamically by the component
    },
    // {
    //   key: 'startTimeFrom',
    //   label: 'pos.shift_open.search.fields.start_time_from',
    //   type: 'dateRange',
    //   range: { startKey: 'startTimeFrom', endKey: 'startTimeTo' },
    //   placeholder: 'pos.shift_open.search.placeholders.select_start_time_from',
    // },
  ];
}

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const POS_SHIFT_CLOSE_DEFAULT_COLUMNS = {
  SHIFT_ID: 'shift_id',
  POS_CODE: 'posCode',
  SHOP_NAME: 'shopName',
  CASHIER_NAME: 'cashierName',
  OPENING_TIME: 'openingTime',
  CLOSING_TIME: 'closingTime',
  OPENING_AMOUNT: 'openingAmount',
  CLOSING_AMOUNT: 'closingAmount',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS = {
  TOTAL_SALES: 'totalOrderAmount',
  STATUS: 'status',
} as const;

/**
 * Action column property name
 */
export const POS_SHIFT_CLOSE_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const POS_SHIFT_CLOSE_DEFAULT_COLUMNS_ORDER: string[] = [
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.SHIFT_ID,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.POS_CODE,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.SHOP_NAME,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CASHIER_NAME,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.OPENING_TIME,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CLOSING_TIME,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.OPENING_AMOUNT,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CLOSING_AMOUNT,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS_ORDER: string[] = [
  POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS.TOTAL_SALES,
  POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS.STATUS,
];

export const POS_SHIFT_CLOSE_COLUMNS: TableColumn[] = [
  {
    label: 'pos.shift_close.table.columns.shift_id',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.SHIFT_ID,
    width: 130,
  },
  {
    label: 'pos.shift_close.table.columns.pos_code',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.POS_CODE,
    width: 100,
  },
  {
    label: 'pos.shift_close.table.columns.shop_name',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.SHOP_NAME,
    width: 130,
  },
  {
    label: 'pos.shift_close.table.columns.cashier_name',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CASHIER_NAME,
    width: 120,
  },
  {
    label: 'pos.shift_close.table.columns.opening_time',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.OPENING_TIME,
    width: 160,
    type: 'custom',
    template: 'dateTemplate',
    sortable: true,
    sortField: 'startTime',
  },
  {
    label: 'pos.shift_close.table.columns.closing_time',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CLOSING_TIME,
    width: 160,
    type: 'custom',
    template: 'dateTemplate',
    sortable: true,
    sortField: 'endTime',
  },
  {
    label: 'pos.shift_close.table.columns.opening_amount',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.OPENING_AMOUNT,
    width: 130,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.table.columns.closing_amount',
    prop: POS_SHIFT_CLOSE_DEFAULT_COLUMNS.CLOSING_AMOUNT,
    width: 130,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.table.columns.total_sales',
    prop: POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS.TOTAL_SALES,
    width: 130,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.table.columns.status',
    prop: POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS.STATUS,
    width: 100,
    type: 'status',
  },
  {
    label: 'pos.shift_close.table.columns.actions',
    prop: POS_SHIFT_CLOSE_ACTION_COLUMN.ACTIONS,
    width: 100,
    type: 'actions',
  },
];

// ============================================================================
// FORM CONFIGURATION FOR ADD-FORM COMPONENT
// ============================================================================

export function getPosShiftCloseFormConfig(
  posShiftCloseService: PosShiftCloseService
): PosShiftCloseFormConfig {
  return {
    title: 'pos.shift_close.detail.title',
    listRoute: '/pos/shift-close',
    headerActions: [
      {
        label: 'common.actions.go_back',
        severity: 'secondary',
        outlined: true,
        action: 'back',
      },
    ],
    formFields: [
      {
        controlName: 'shift_id',
        label: 'pos.shift_close.detail.fields.shift_id',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'cashierName',
        label: 'pos.shift_close.detail.fields.cashier_name',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'posCode',
        label: 'pos.shift_close.detail.fields.pos_code',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'shopName',
        label: 'pos.shift_close.detail.fields.shop_name',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'shopAddress',
        label: 'pos.shift_close.detail.fields.shop_address',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'openingTime',
        label: 'pos.shift_close.detail.fields.opening_time',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      {
        controlName: 'closingTime',
        label: 'pos.shift_close.detail.fields.closing_time',
        type: 'text',
        readonly: true,
        section: 'pos.shift_close.detail.sections.shift_info',
      },
      // {
      //   controlName: 'openingAmount',
      //   label: 'pos.shift_close.detail.fields.opening_amount',
      //   type: 'currency',
      //   readonly: true,
      //   section: 'pos.shift_close.detail.sections.shift_info'
      // },
      // {
      //   controlName: 'closingAmount',
      //   label: 'pos.shift_close.detail.fields.closing_amount',
      //   type: 'currency',
      //   readonly: true,
      //   section: 'pos.shift_close.detail.sections.shift_info'
      // }
    ],
    summarySection: {
      title: 'pos.shift_close.detail.sections.sales_summary',
      show: true,
      items: [
        {
          label: 'pos.shift_close.detail.table.summary.total_quantity',
          prop: 'totalQuantity',
          value: 0,
          formatter: 'number',
        },
        {
          label: 'pos.shift_close.detail.table.summary.total',
          prop: 'totalAmount',
          value: 0,
          formatter: 'currency',
          currency: 'VND',
        },
        {
          label: 'pos.shift_close.detail.table.summary.total_net',
          prop: 'totalNetAmount',
          value: 0,
          formatter: 'currency',
          currency: 'VND',
        },
      ],
    },
  };
}

export const POS_SHIFT_CLOSE_INVOICE_COLUMNS: TableColumn[] = [
  {
    label: 'pos.shift_close.detail.table.invoices.pos_invoice',
    prop: 'posInvoice',
    width: 120,
  },
  {
    label: 'pos.shift_close.detail.table.invoices.invoice_date',
    prop: 'invoiceDate',
    width: 160,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'pos.shift_close.detail.table.invoices.payment_method',
    prop: 'paymentMethod',
    width: 120,
  },
  {
    label: 'pos.shift_close.detail.table.invoices.invoice_amount',
    prop: 'invoiceAmount',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
];

export const POS_SHIFT_CLOSE_PAYMENT_METHODS_COLUMNS: TableColumn[] = [
  {
    label: 'pos.shift_close.detail.table.payment_methods.method',
    prop: 'paymentMethod',
    width: 120,
  },
  {
    label: 'pos.shift_close.detail.table.payment_methods.amount',
    prop: 'paymentAmount',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
];

export const POS_SHIFT_CLOSE_PAYMENT_RECONCILIATION_COLUMNS: TableColumn[] = [
  {
    label: 'pos.shift_close.detail.table.reconciliation.payment_methods',
    prop: 'paymentMethod',
    width: 120,
  },
  {
    label: 'pos.shift_close.detail.table.reconciliation.opening_amount',
    prop: 'openingAmount',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.detail.table.reconciliation.estimated_amount',
    prop: 'estimatedAmount',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.detail.table.reconciliation.amount_paid',
    prop: 'closingAmount',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_close.detail.table.reconciliation.difference',
    prop: 'difference',
    width: 140,
    type: 'custom',
    template: 'currencyTemplate',
  },
];
